import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV2Entity,
  type CompositionV3Ent,
  type PayV3Req,
} from '@revelio/data-access';

import { environment } from '../../../../../environments/environment';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import {
  getCompositionV3Corrections,
  getCompositionV3Filters,
} from '../../../filters/utils/composition-v3/getCompositionV3Filters';
import {
  getCompositionV3Variables,
  normalizeCompositionEffectiveFilters,
} from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getV3Taxonomies } from '../../../filters/utils/get-v3-taxonomies';
import {
  mapPositionsEntitiesToCompositionV3,
  positionsEntityKindToEntKind,
  stripEntitiesFromFilters,
} from '../../../filters/utils/role-skill-ent-kind';
import { useCompositionV3QueryWithStatus } from '../../DataResolver/use-composition-v3-query';
import { usePayData } from '../compensation/use-pay-data';
import { isCompositionV3FullMetrics } from '../composition/composition-v3/composition-v3-graphql-response';
import { DataProviderQueryStatus } from '../types';
import { combineQueryStatuses } from '../utils/combine-query-statuses';
import { getSegmentEntityType } from './getSegmentEntityType';

const isProd = environment.production;

const MAX_SEGMENT_ENTITIES = isProd ? 6 : 10;

type PayAndCompositionParams = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: 'geography' | 'role';
  entitiesOverride?: CompositionV2Entity[];
};

export function usePayAndCompositionQueries({
  primaryDimension,
  secondaryDimension,
  entitiesOverride,
}: PayAndCompositionParams) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { usesStateBreakdownOverMsa, geographySelectionListIds } =
    useFilterContext();

  const compositionVariablesV3 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    geographySelectionListIds,
  });

  const compositionEntitiesV3 = (() => {
    if (Array.isArray(entitiesOverride) && entitiesOverride.length > 0) {
      return mapPositionsEntitiesToCompositionV3(entitiesOverride) ?? [];
    }
    if (Array.isArray(compositionVariablesV3.entities)) {
      return compositionVariablesV3.entities;
    }
    return compositionVariablesV3.entities
      ? [compositionVariablesV3.entities]
      : [];
  })();

  const compositionVariablesFinalV3 = {
    ...compositionVariablesV3,
    entities: compositionEntitiesV3,
  };

  const pauseCompositionV3 =
    compositionVariablesFinalV3.entities.length === 0 ||
    !compositionVariablesFinalV3.filters?.start_date ||
    !compositionVariablesFinalV3.filters?.end_date;

  const [{ data: compositionDataUnion, status: compositionStatusUnion }] =
    useCompositionV3QueryWithStatus({
      query: COMPOSITION_GET_DATA_V3,
      variables: { req: compositionVariablesFinalV3 },
      pause: pauseCompositionV3,
    });

  const compositionRes = compositionDataUnion?.composition_v3;

  const segmentEntityType = compositionRes
    ? getSegmentEntityType({
        secondaryDimension,
        compositionFilters: compositionVariablesFinalV3.filters,
      })
    : null;
  const segmentEntityTypeV3 =
    segmentEntityType !== null
      ? positionsEntityKindToEntKind(segmentEntityType)
      : null;

  const segmentPayEntities = (() => {
    const entities: CompositionV3Ent[] = [];
    if (
      !compositionRes?.entities?.length ||
      segmentEntityType === null ||
      segmentEntityTypeV3 === null
    ) {
      return entities;
    }
    const seen = new Set<number>();
    for (const ent of compositionRes.entities) {
      const segments =
        (isCompositionV3FullMetrics(ent.metrics) ? ent.metrics : undefined)?.[
          secondaryDimension
        ] ?? [];
      for (const seg of segments) {
        if (seen.size >= MAX_SEGMENT_ENTITIES) break;
        const id = (seg as { id: number }).id;
        if (id !== undefined && !seen.has(id)) {
          seen.add(id);
          entities.push({ EntID: id, EntType: segmentEntityTypeV3 });
        }
      }
      if (seen.size >= MAX_SEGMENT_ENTITIES) break;
    }
    return entities;
  })();

  const payFilterPrimaryDimension =
    secondaryDimension === 'geography' ? FilterId.GEOGRAPHY : FilterId.ROLE;

  const normalizedFilters =
    normalizeCompositionEffectiveFilters(effectiveFilters);
  const payFilters = getCompositionV3Filters({
    primaryDimension: payFilterPrimaryDimension,
    isSnapshot: false,
    effectiveFilters: normalizedFilters,
  });

  const payVariables: PayV3Req = {
    entities: segmentPayEntities,
    filters: stripEntitiesFromFilters(payFilters, segmentPayEntities),
    corrections: getCompositionV3Corrections(normalizedFilters),
    ...getV3Taxonomies(normalizedFilters),
    ...(usesStateBreakdownOverMsa && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  const compositionOk =
    !pauseCompositionV3 &&
    compositionStatusUnion === DataProviderQueryStatus.Success;

  const pausePay =
    pauseCompositionV3 ||
    !compositionOk ||
    !compositionRes ||
    segmentPayEntities.length === 0 ||
    !payVariables.filters.start_date ||
    !payVariables.filters.end_date;

  const { data: payData, status: payStatus } = usePayData({
    variant: 'full',
    req: payVariables,
    pause: pausePay,
  });

  const status = combineQueryStatuses([compositionStatusUnion, payStatus]);

  return {
    payData: payData?.pay_v3 ?? undefined,
    compositionData: compositionDataUnion?.composition_v3,
    status,
  };
}
